/**
 * Central design token definitions for the Rugira web client.
 * All tokens should be declared here and consumed elsewhere
 * to keep typography, spacing, radius, and semantic colours aligned.
 */

export type NotificationSeverityName =
  | "info"
  | "success"
  | "warning"
  | "error"
  | "critical";

export interface NotificationSeverityVisualTokens {
  surface: string;
  border: string;
  text: string;
  accent: string;
  icon: string;
}

export interface DesignTokens {
  typography: {
    fontFamily: string;
    size: {
      xs: string;
      sm: string;
      base: string;
      md: string;
      lg: string;
      xl: string;
      "2xl": string;
      "3xl": string;
    };
    weight: {
      regular: number;
      medium: number;
      semibold: number;
      bold: number;
    };
  };
  spacing: {
    px: Record<"xs" | "sm" | "md" | "lg" | "xl" | "2xl" | "3xl", number>;
    rem: Record<"xs" | "sm" | "md" | "lg" | "xl" | "2xl" | "3xl", string>;
  };
  radius: {
    base: string;
    sm: string;
    md: string;
    lg: string;
    pill: string;
  };
  shadow: {
    soft: string;
  };
  accentRule: {
    subtle: string;
    strong: string;
    brand: string;
  };
  notification: {
    severity: Record<NotificationSeverityName, NotificationSeverityVisualTokens>;
  };
}

const pxToRem = (value: number): string => `${value / 16}rem`;

const spacingPx = {
  xs: 4,
  sm: 8,
  md: 12,
  lg: 16,
  xl: 20,
  "2xl": 32,
  "3xl": 48,
} as const;

const notificationSeverity: Record<
  NotificationSeverityName,
  NotificationSeverityVisualTokens
> = {
  info: {
    surface: "#EFF6FF",
    border: "#93C5FD",
    text: "#1E40AF",
    accent: "#2563EB",
    icon: "#1D4ED8",
  },
  success: {
    surface: "#F0FDF4",
    border: "#86EFAC",
    text: "#15803D",
    accent: "#16A34A",
    icon: "#15803D",
  },
  warning: {
    surface: "#FFFBEB",
    border: "#FDE68A",
    text: "#A16207",
    accent: "#F59E0B",
    icon: "#B45309",
  },
  error: {
    surface: "#FEF2F2",
    border: "#FCA5A5",
    text: "#DC2626",
    accent: "#F87171",
    icon: "#B91C1C",
  },
  critical: {
    surface: "#FFE4E6",
    border: "#FB7185",
    text: "#9F1239",
    accent: "#E10600",
    icon: "#BE123C",
  },
};

export const designTokens: DesignTokens = {
  typography: {
    fontFamily: "'Inter', sans-serif",
    size: {
      xs: "12px",
      sm: "13px",
      base: "14px",
      md: "16px",
      lg: "20px",
      xl: "22px",
      "2xl": "28px",
      "3xl": "32px",
    },
    weight: {
      regular: 400,
      medium: 500,
      semibold: 600,
      bold: 700,
    },
  },
  spacing: {
    px: spacingPx,
    rem: Object.fromEntries(
      Object.entries(spacingPx).map(([key, value]) => [key, pxToRem(value)]),
    ) as Record<
      keyof typeof spacingPx,
      string
    >,
  },
  radius: {
    base: "10px",
    sm: "8px",
    md: "10px",
    lg: "14px",
    pill: "9999px",
  },
  shadow: {
    soft: "0 6px 18px rgba(0, 0, 0, 0.06)",
  },
  accentRule: {
    subtle: "#E5E7EB",
    strong: "#D1D5DB",
    brand: "#E10600",
  },
  notification: {
    severity: notificationSeverity,
  },
};

export const notificationSeverityTokens = notificationSeverity;

export type NotificationSeverityVisualMap = typeof notificationSeverityTokens;
